import { useEffect, useId, useRef } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import './ConsentPanel.css'

export interface ConsentPurpose {
  id: string
  label: ReactNode
  description: ReactNode
  /** Finalité indispensable au site : affichée cochée et verrouillée. */
  required?: boolean
}

export interface ConsentPanelProps {
  purposes: ConsentPurpose[]
  /** État courant des cases, par identifiant de finalité. */
  value: Record<string, boolean>
  onToggle: (id: string, checked: boolean) => void
  onSave: () => void
  onAcceptAll: () => void
  onRejectAll: () => void
  onClose: () => void
  title?: ReactNode
  intro?: ReactNode
  footer?: ReactNode
  saveLabel?: string
  acceptLabel?: string
  rejectLabel?: string
  closeLabel?: string
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Panneau de réglage détaillé du consentement, ouvert depuis « Personnaliser »
 * ou depuis le lien permanent du pied de page.
 *
 * Contrairement au bandeau, c'est une vraie boîte de dialogue modale : le
 * visiteur l'a ouverte délibérément, le focus y est donc enfermé tant qu'elle
 * est ouverte et restitué à l'élément d'origine à la fermeture.
 *
 * `onClose` (croix, Échap, clic sur le fond) n'enregistre **aucun** choix :
 * refermer le panneau n'est ni un accord ni un refus. C'est à l'appelant de
 * réafficher le bandeau si rien n'a encore été décidé.
 *
 * Les finalités optionnelles doivent arriver décochées : le composant affiche
 * `value` tel quel, c'est donc à l'appelant de ne jamais pré-cocher autre chose
 * qu'une finalité `required`.
 */
export function ConsentPanel({
  purposes,
  value,
  onToggle,
  onSave,
  onAcceptAll,
  onRejectAll,
  onClose,
  title = 'Vos préférences de cookies',
  intro,
  footer,
  saveLabel = 'Enregistrer mes choix',
  acceptLabel = 'Tout accepter',
  rejectLabel = 'Tout refuser',
  closeLabel = 'Fermer sans enregistrer',
}: ConsentPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const baseId = useId()
  const titleId = `${baseId}-title`

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    panel.querySelector<HTMLElement>(FOCUSABLE)?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (!panel) return

      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusables.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      // `isConnected` : l'élément d'origine peut avoir été démonté entre-temps
      // — c'est le cas du bouton « Personnaliser » du bandeau, que l'ouverture
      // de ce panneau fait disparaître. Lui rendre le focus ne ferait rien ;
      // c'est alors à l'appelant de décider où le poser.
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [onClose])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // Un simple `onClick` sur le fond fermerait aussi le panneau lorsqu'une
  // sélection de texte commencée à l'intérieur se relâche à l'extérieur : le
  // clic est alors attribué au plus proche ancêtre commun, c'est-à-dire le
  // fond. On se fie donc au `mousedown`, qui, lui, a bien eu lieu sur le fond.
  function handleOverlayMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <div className="azoya-consent-overlay" onMouseDown={handleOverlayMouseDown}>
      <div
        className="azoya-consent-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        <button
          type="button"
          className="azoya-consent-panel-close"
          aria-label={closeLabel}
          onClick={onClose}
        >
          ×
        </button>

        <h2 id={titleId} className="azoya-consent-panel-title">
          {title}
        </h2>
        {intro && <p className="azoya-consent-panel-intro">{intro}</p>}

        <ul className="azoya-consent-panel-purposes">
          {purposes.map((purpose) => {
            const descriptionId = `${baseId}-${purpose.id}-description`
            return (
              <li key={purpose.id} className="azoya-consent-panel-purpose">
                <Checkbox
                  label={purpose.label}
                  className="azoya-consent-panel-purpose-toggle"
                  checked={purpose.required ? true : Boolean(value[purpose.id])}
                  disabled={purpose.required}
                  aria-describedby={descriptionId}
                  onChange={(event) => onToggle(purpose.id, event.target.checked)}
                />
                <p id={descriptionId} className="azoya-consent-panel-purpose-description">
                  {purpose.description}
                </p>
              </li>
            )
          })}
        </ul>

        {footer && <div className="azoya-consent-panel-footer">{footer}</div>}

        <div className="azoya-consent-panel-actions">
          <Button variant="light" className="azoya-consent-panel-save" onClick={onSave}>
            {saveLabel}
          </Button>
          <Button onClick={onRejectAll}>{rejectLabel}</Button>
          <Button onClick={onAcceptAll}>{acceptLabel}</Button>
        </div>
      </div>
    </div>
  )
}
